<template>
  <div class="fade-in p-3 sm:p-4 max-w-screen-xl w-full mx-auto">
    <div class="h-full">
      <!-- Back button and title -->
      <div
        class="flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-2 space-y-3 sm:space-y-0 sm:space-x-2"
      >
        <div class="flex items-center">
          <MountainSnow class="w-5 h-5 mr-2 text-gray-700" />
          <h1 class="text-xl font-normal text-gray-800 min-w-[120px]">
            {{ formattedExpeditionName }}
          </h1>
        </div>
        <div class="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0"></div>

        <div class="flex flex-wrap flex-grow justify-end gap-2">
          <div
            class="min-w-[120px] text-gray-500 flex items-center border border-gray-200 rounded-md py-1 bg-white text-sm h-10 px-4"
          >
            Started:
            {{ currentExpeditionStartDate }}
          </div>
          
          <div
            v-if="expeditionsStore.adventurersProgress?.week"
            class="min-w-[120px] text-gray-500  flex items-center border border-gray-200 rounded-md py-1 bg-white text-sm h-10 px-4"
          >
            <CalendarClock class="w-4 h-4 mr-2" />
            On week {{ expeditionsStore.adventurersProgress?.week }}
          </div>

          <component
            :is="isBlurred ? 'div' : 'router-link'"
            :to="
              isBlurred
                ? undefined
                : `/profile/${expeditionsStore.adventurersProgress?.main_guide.username}`
            "
            v-if="expeditionsStore.adventurersProgress?.main_guide"
            class="text-gray-500 flex items-center border border-gray-200 rounded-md py-1 bg-white text-sm h-10 pl-3 pr-4"
          >
            <div
              v-if="expeditionsStore.adventurersProgress?.main_guide.avatar_image"
              class="w-6 h-6 mr-2 rounded-full overflow-hidden"
            >
              <img
                :src="expeditionsStore.adventurersProgress?.main_guide.avatar_image"
                class="w-full h-full object-cover"
                :class="{ 'blur-md select-none': isBlurred }"
              />
            </div>
            <div
              v-else
              class="w-6 h-6 mr-2 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"
            >
              <div
                class="w-full h-full flex items-center justify-center"
                :class="{ 'blur-md select-none': isBlurred }"
              >
                <UserRound class="w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div class="flex flex-col min-w-[120px]">
              <span class="text-gray-800 font-medium" :class="{ 'blur-md select-none': isBlurred }"
                >{{ expeditionsStore.adventurersProgress?.main_guide.first_name }}
                {{ expeditionsStore.adventurersProgress?.main_guide.last_name }}</span
              >
              <span class="text-gray-400 text-xs -mt-1">Main Guide</span>
            </div>
          </component>
        </div>
      </div>

      <!-- Adventurers table -->
      <div
        v-if="progressLoadFailed"
        class="mt-4 border border-gray-200 rounded-xl bg-white p-6 text-center text-sm text-gray-500"
      >
        Curriculum progress doesn't exist for members of this expedition (might be a legacy
        expedition).
      </div>
      <ExpeditionAdventurersTable v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useExpeditionsStore } from '@/stores/expeditions'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { MountainSnow, UserRound, CalendarClock } from 'lucide-vue-next'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import { formatDate } from '@/utils/formatDate'
import ExpeditionAdventurersTable from '@/components/manage/ExpeditionAdventurersTable.vue'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const expeditionsStore = useExpeditionsStore()
const route = useRoute()
const expeditionId = computed(() => route.params.id as string)
const { isBlurred } = usePrivacyMode()

const currentExpeditionStartDate = computed(() => {
  const expedition = expeditionsStore.expeditions.find(
    (e) => e.id.toString() === expeditionId.value
  )
  return expedition?.start_date ? formatDate(expedition.start_date) : null
})

const formattedExpeditionName = computed(() => {
  const progress = expeditionsStore.adventurersProgress as any
  return formatExpeditionName(progress?.name)
})

watch(
  formattedExpeditionName,
  (newName) => {
    if (newName) {
      document.title = `${newName} | Outtalent Campus`
    }
  },
  { immediate: true }
)

const progressLoadFailed = ref(false)

const loadExpeditionData = async () => {
  const id = expeditionId.value
  expeditionsStore.adventurersProgress = null
  progressLoadFailed.value = false
  await expeditionsStore.fetchAdventurersProgress(id)
  if (id === expeditionId.value) progressLoadFailed.value = !expeditionsStore.adventurersProgress
}

watch(
  expeditionId,
  () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadExpeditionData()
  },
  { immediate: true }
)
</script>
